import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Building2, Users, Search, RefreshCw, CreditCard, 
  TrendingUp, Eye, ChevronRight, Gift
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminOrganizationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [stats, setStats] = useState({ total: 0, orgFunded: 0, athleteFunded: 0 });
  const [searchQuery, setSearchQuery] = useState('');

  const adminKey = sessionStorage.getItem('aura_admin_key');

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/organizations`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      
      setOrganizations(response.data.organizations || []);
      setStats(response.data.stats || { total: 0, orgFunded: 0, athleteFunded: 0 });
    } catch (error) {
      toast.error('Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantOrgAccess = async (orgId, orgName) => {
    try {
      await axios.post(`${API}/api/admin/grant-org-free-access`, 
        { org_id: orgId, org_name: orgName, reason: 'Admin granted' },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      toast.success(`Free access granted to ${orgName}`);
      fetchOrganizations();
    } catch (error) {
      toast.error('Failed to grant access');
    }
  };

  const filteredOrgs = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.contact_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 50) return 'text-amber-400';
    return 'text-red-400';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
              Organizations
            </h1>
            <p className="text-aura-text-muted text-sm mt-1">
              Manage teams, schools, and academies
            </p>
          </div>
          <Button onClick={fetchOrganizations} variant="outline" className="border-white/10 text-white">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-aura-text-muted uppercase">Total Organizations</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.total}</p>
                </div>
                <Building2 className="w-10 h-10 text-purple-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-aura-text-muted uppercase">Org-Funded</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.orgFunded}</p>
                  <p className="text-xs text-green-400 mt-1">Premium orgs</p>
                </div>
                <CreditCard className="w-10 h-10 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-aura-text-muted uppercase">Athlete-Funded</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.athleteFunded}</p>
                  <p className="text-xs text-amber-400 mt-1">Athletes pay</p>
                </div>
                <Users className="w-10 h-10 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-aura-text-muted" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search organizations..."
                className="pl-10 bg-aura-bg border-white/10 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Organizations List */}
        <div className="space-y-4">
          {loading ? (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <RefreshCw className="w-8 h-8 text-aura-primary animate-spin mx-auto" />
              </CardContent>
            </Card>
          ) : filteredOrgs.length === 0 ? (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <Building2 className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
                <p className="text-aura-text-muted">No organizations found</p>
              </CardContent>
            </Card>
          ) : (
            filteredOrgs.map((org) => (
              <Card key={org._id || org.id} className="bg-aura-surface border-white/10 hover:border-white/20 transition-all">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Org Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                        <Building2 className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-heading font-bold text-white">{org.name}</h3>
                        <p className="text-sm text-aura-text-muted">{org.contact_email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`${
                            org.billing_path === 'org_funded' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-amber-500/20 text-amber-400'
                          } border-0 text-xs`}>
                            {org.billing_path === 'org_funded' ? 'Org Pays' : 'Athletes Pay'}
                          </Badge>
                          {org.free_access && (
                            <Badge className="bg-blue-500/20 text-blue-400 border-0 text-xs">
                              Free Access
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{org.member_count || 0}</p>
                        <p className="text-xs text-aura-text-muted">Members</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{org.active_count || 0}</p>
                        <p className="text-xs text-aura-text-muted">Active</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1">
                          <TrendingUp className={`w-4 h-4 ${getHealthColor(org.health_score || 0)}`} />
                          <p className={`text-2xl font-bold ${getHealthColor(org.health_score || 0)}`}>
                            {org.health_score || 0}
                          </p>
                        </div>
                        <p className="text-xs text-aura-text-muted">Health</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {!org.free_access && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-green-500/50 text-green-400"
                          onClick={() => handleGrantOrgAccess(org._id || org.id, org.name)}
                        >
                          <Gift className="w-4 h-4 mr-1" /> Grant
                        </Button>
                      )}
                      <Link to={`/admin/organizations/${org._id || org.id}`}>
                        <Button variant="outline" size="sm" className="border-white/10 text-white">
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Health Progress */}
                  <div className="mt-4 pt-4 border-t border-white/5">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-aura-text-muted">Organization Health</span>
                      <span className={getHealthColor(org.health_score || 0)}>
                        {org.health_score || 0}%
                      </span>
                    </div>
                    <Progress value={org.health_score || 0} className="h-1.5 bg-white/10" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminOrganizationsPage;
